import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { ChevronUp, ChevronDown, Minus } from 'lucide-react-native';
import { useUnits } from '@/lib/useUnits';

const FRESHNESS_COLORS: Record<string, string> = {
  green: '#22c55e',
  blue: '#3b82f6',
  amber: '#f59e0b',
  red: '#ef4444',
};

type SortBy = 'strength' | 'relative' | 'duelsWon' | 'volume';

type Props = {
  rank: number | null;
  totalUsers: number;
  strengthScore: number | null;
  previousRank: number | null;
  freshnessBand: string | null;
  displayName: string;
  onRankPress?: () => void;
  sortBy?: SortBy;
  totalVolume?: number | null;
  wins?: number | null;
  losses?: number | null;
  userUnits?: string;
  dotsScore?: number | null;
};

export default function PinnedRankCard({
  rank,
  totalUsers,
  strengthScore,
  previousRank,
  freshnessBand,
  displayName,
  onRankPress,
  sortBy = 'strength',
  totalVolume: rawVolume = null,
  wins = null,
  losses = null,
  userUnits: _userUnits = 'lbs',
  dotsScore = null,
}: Props) {
  const { theme } = useTheme();
  const { displayWeight, userUnits: liveUnits } = useUnits();
  const userUnits = liveUnits;
  const totalVolume = rawVolume != null ? displayWeight(rawVolume, 'lbs') : null;

  if (rank == null) {
    return (
      <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
        <Text style={[styles.noRankText, { color: theme.colors.textSecondary }]}>
          Complete baseline to enter rankings
        </Text>
      </View>
    );
  }

  // Hide percentile when < 5 users or when user is last place (Top 100% is misleading)
  const showPercentile = totalUsers >= 5 && rank < totalUsers;
  const percentile = showPercentile ? Math.max(1, Math.round((rank / totalUsers) * 100)) : null;
  const percentileLabel = percentile != null ? `Top ${percentile}%` : `#${rank}`;

  // Don't show rank changes when total users < 5 — too volatile
  const movement = previousRank != null && totalUsers >= 5 ? previousRank - rank : 0;
  const stripeColor = FRESHNESS_COLORS[freshnessBand ?? ''] ?? theme.colors.border;

  return (
    <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
      <View style={[styles.stripe, { backgroundColor: stripeColor }]} />
      <View style={styles.content}>
        <TouchableOpacity
          style={styles.rankBlock}
          onPress={onRankPress}
          disabled={!onRankPress}
          activeOpacity={0.6}
          accessibilityRole="button"
          accessibilityLabel={`Scroll to your rank, ${rank} of ${totalUsers}`}
        >
          <Text style={[styles.rankNumber, { color: theme.colors.text }]}>#{rank}</Text>
          <Text style={[styles.ofTotal, { color: theme.colors.textSecondary }]}>of {totalUsers}</Text>
        </TouchableOpacity>
        <View style={styles.center}>
          <Text style={[styles.percentile, { color: theme.colors.accent }]}>{percentileLabel}</Text>
          <Text style={[styles.name, { color: theme.colors.textSecondary }]}>{displayName}</Text>
        </View>
        <View style={styles.right}>
          <Text style={[styles.score, { color: theme.colors.text }]}>
            {sortBy === 'strength'
              ? (strengthScore != null ? Math.round(strengthScore).toLocaleString() : '—')
              : sortBy === 'relative'
              ? (dotsScore != null && dotsScore > 0 ? Math.round(dotsScore).toLocaleString() : '—')
              : sortBy === 'volume'
              ? (totalVolume != null ? Math.round(totalVolume).toLocaleString() : '—')
              : (() => {
                  const total = (wins ?? 0) + (losses ?? 0);
                  if (total === 0) return '—';
                  return `${Math.round(((wins ?? 0) / total) * 100)}%`;
                })()
            }
          </Text>
          <Text style={[styles.scoreLabel, { color: theme.colors.textSecondary }]}>
            {sortBy === 'strength' ? 'STR' : sortBy === 'relative' ? 'DOTS' : sortBy === 'volume' ? userUnits.toUpperCase() : `${wins ?? 0}W-${losses ?? 0}L`}
          </Text>
          {previousRank != null && (
            <View style={styles.movementRow}>
              {movement > 0 ? (
                <>
                  <ChevronUp size={14} color="#22c55e" />
                  <Text style={[styles.movementText, { color: '#22c55e' }]}>+{movement}</Text>
                </>
              ) : movement < 0 ? (
                <>
                  <ChevronDown size={14} color="#ef4444" />
                  <Text style={[styles.movementText, { color: '#ef4444' }]}>{movement}</Text>
                </>
              ) : (
                <>
                  <Minus size={14} color="#9ca3af" />
                  <Text style={[styles.movementText, { color: '#9ca3af' }]}>—</Text>
                </>
              )}
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 4,
    borderRadius: 2,
    borderWidth: 1,
    overflow: 'hidden',
    flexDirection: 'row',
  },
  stripe: { width: 4 },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 14,
  },
  rankBlock: { alignItems: 'center', marginRight: 14 },
  rankNumber: { fontSize: 28, fontWeight: '900' },
  ofTotal: { fontSize: 11, fontWeight: '500' },
  center: { flex: 1 },
  percentile: { fontSize: 16, fontWeight: '800' },
  name: { fontSize: 12, fontWeight: '500', marginTop: 2 },
  right: { alignItems: 'flex-end' },
  score: { fontSize: 22, fontWeight: '900' },
  scoreLabel: { fontSize: 11, fontWeight: '600' },
  movementRow: { flexDirection: 'row', alignItems: 'center', marginTop: 2 },
  movementText: { fontSize: 11, fontWeight: '700' },
  noRankText: { fontSize: 14, fontWeight: '600', padding: 16, textAlign: 'center' },
});

